import React from 'react';
import NeonDivider from './NeonDivider';

const EventsSection = () => {
  return (
    <section id="events" className="py-20 px-4 bg-gradient-to-b from-black via-black/95 to-black">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-logoSans text-ivory mb-4">
            PRIVATE EVENTS
          </h2>
          <p className="text-gray text-lg max-w-2xl mx-auto">
            Host your celebration at Billy's WingCo. Our neon-lit atmosphere and legendary wings 
            make every event unforgettable.
          </p>
        </div>

        <NeonDivider color="neonRed" />

        <div className="grid lg:grid-cols-2 gap-12 mb-16">
          <div className="space-y-8">
            <h3 className="text-2xl md:text-3xl font-logoSans text-ivory">
              PERFECT FOR
            </h3>
            
            <div className="grid sm:grid-cols-2 gap-4">
              {[
                { icon: "🎂", title: "Birthday Parties", desc: "Celebrate in neon style" },
                { icon: "🏢", title: "Corporate Events", desc: "Team building & meetings" },
                { icon: "🎓", title: "Graduations", desc: "Mark achievements" },
                { icon: "⚽", title: "Sports Viewing", desc: "Watch the big game" }
              ].map((event, index) => (
                <div key={index} className="p-4 border border-gray/20 rounded-lg bg-black/30 backdrop-blur-sm">
                  <div className="text-2xl mb-2">{event.icon}</div>
                  <h4 className="text-ivory font-semibold mb-1">{event.title}</h4>
                  <p className="text-gray text-sm">{event.desc}</p>
                </div>
              ))}
            </div>

            <div className="space-y-6">
              <h4 className="text-xl font-logoSans text-ivory">Venue Features</h4>
              <div className="grid sm:grid-cols-2 gap-4">
                {[
                  "Private dining area",
                  "Multiple big screens",
                  "Sound system access",
                  "Customized lighting",
                  "Dedicated server",
                  "Free parking"
                ].map((feature, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <div className="w-2 h-2 bg-neonBlue rounded-full"></div>
                    <span className="text-gray">{feature}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="space-y-8">
            <h3 className="text-2xl md:text-3xl font-logoSans text-ivory">
              PARTY PACKAGES
            </h3>

            <div className="space-y-6">
              {/* Standard Package */}
              <div className="p-6 border border-neonBlue/20 rounded-xl bg-black/30 backdrop-blur-sm">
                <div className="flex justify-between items-start mb-4">
                  <h4 className="text-xl font-logoSans text-ivory">Standard Package</h4>
                  <span className="text-neonBlue font-bold text-lg">$25/person</span>
                </div>
                <ul className="space-y-2 text-gray text-sm">
                  <li>• 1 pound of wings per person</li>
                  <li>• Choice of 3 sauces</li>
                  <li>• Fries & onion rings</li>
                  <li>• 2-hour Reserved Section</li>
                  <li>• Minimum 15 people</li>
                </ul>
              </div>

              {/* Premium Package */}
              <div className="p-6 border border-neonRed/20 rounded-xl bg-black/30 backdrop-blur-sm relative">
                <div className="absolute -top-3 left-4">
                  <span className="bg-neonRed text-white px-3 py-1 rounded-full text-xs font-semibold">MOST POPULAR</span>
                </div>
                <div className="flex justify-between items-start mb-4">
                  <h4 className="text-xl font-logoSans text-ivory">Premium Package</h4>
                  <span className="text-neonRed font-bold text-lg">$35/person</span>
                </div>
                <ul className="space-y-2 text-gray text-sm">
                  <li>• 1 pound of wings per person</li>
                  <li>• Unlimited sauce selection</li>
                  <li>• Appetizer platter</li>
                  <li>• 3-hour Reserved Section</li>
                  <li>• Dedicated event coordinator</li>
                  <li>• Minimum 12 people</li>
                </ul>
              </div>

              {/* Deluxe Package */}
              <div className="p-6 border border-neonBlue/20 rounded-xl bg-black/30 backdrop-blur-sm">
                <div className="flex justify-between items-start mb-4">
                  <h4 className="text-xl font-logoSans text-ivory">Deluxe Package</h4>
                  <span className="text-neonBlue font-bold text-lg">$45/person</span>
                </div>
                <ul className="space-y-2 text-gray text-sm">
                  <li>• 1 pound of wings per person</li>
                  <li>• Full appetizer selection</li>
                  <li>• Burger & sides options</li>
                  <li>• 4-hour Reserved Section</li>
                  <li>• Two Bar Drinks Included</li>
                  <li>• Minimum 10 people</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        <div className="text-center space-y-8">
          <div>
            <h3 className="text-2xl md:text-3xl font-logoSans text-ivory mb-4">
              READY TO BOOK YOUR EVENT?
            </h3>
            <p className="text-gray max-w-2xl mx-auto mb-8">
              Contact our events team to discuss your needs and check availability. 
              We recommend booking at least 2 weeks in advance.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-4xl mx-auto">
            <a
              href="tel:+14162659223"
              className="p-6 border border-neonBlue/20 rounded-xl bg-black/30 backdrop-blur-sm 
                         hover:border-neonBlue/40 transition-all duration-200 text-center"
            >
              <div className="w-12 h-12 bg-neonBlue/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-6 h-6 text-neonBlue" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
              </div>
              <h4 className="text-ivory font-semibold mb-2">Call Us</h4>
              <p className="text-neonBlue">(416) 265-9223</p>
            </a>

            <a
              href="mailto:events@billyswingco.com"
              className="p-6 border border-neonRed/20 rounded-xl bg-black/30 backdrop-blur-sm 
                         hover:border-neonRed/40 transition-all duration-200 text-center"
            >
              <div className="w-12 h-12 bg-neonRed/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-6 h-6 text-neonRed" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <h4 className="text-ivory font-semibold mb-2">Email Us</h4>
              <p className="text-neonRed">events@billyswingco.com</p>
            </a>

            <a
              href="#contact"
              className="p-6 border border-neonBlue/20 rounded-xl bg-black/30 backdrop-blur-sm 
                         hover:border-neonBlue/40 transition-all duration-200 text-center sm:col-span-2 lg:col-span-1"
            >
              <div className="w-12 h-12 bg-neonBlue/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-6 h-6 text-neonBlue" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
              <h4 className="text-ivory font-semibold mb-2">Visit Us</h4>
              <p className="text-neonBlue">See Our Venue</p>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default EventsSection; 